import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../../store';
import { setDocs, setLoading } from '../../store/knowledgeBaseSlice';
import { knowledgeBaseAPI } from '../../api/services';
import KBEditorModal from '../Employee/Workspace/KBEditorModal';

const VIS_COLORS: Record<string, string> = {
  private: 'bg-gray-100 text-gray-600',
  department: 'bg-blue-100 text-blue-700',
  team: 'bg-blue-100 text-blue-700',
  public: 'bg-green-100 text-green-700',
};

const cap = (s: string) => s ? s.charAt(0).toUpperCase() + s.slice(1) : '';
const DEFAULT_ICONS = ['📄', '💡', '📚', '📌'];
const randomIcon = () => DEFAULT_ICONS[Math.floor(Math.random() * DEFAULT_ICONS.length)];

const timeAgo = (d: string) => {
  const diff = Date.now() - new Date(d).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'Just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 7) return `${days}d ago`;
  return new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
};

const KnowledgeBaseList: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { docs, isLoading } = useSelector((s: RootState) => s.knowledgeBase);

  const [filter, setFilter] = useState<'all' | 'mine' | 'department' | 'public'>('all');
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({ title: '', visibility: 'private' });
  const [submitting, setSubmitting] = useState(false);
  const [openDocId, setOpenDocId] = useState<string | null>(null);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search), 300);
    return () => clearTimeout(t);
  }, [search]);

  const load = useCallback(async () => {
    dispatch(setLoading(true));
    try {
      const params: Record<string, string> = {};
      if (filter !== 'all') params.filter = filter;
      if (debouncedSearch) params.search = debouncedSearch;
      const data = await knowledgeBaseAPI.list(params);
      dispatch(setDocs(data));
    } catch { /* ignore */ }
    finally { dispatch(setLoading(false)); }
  }, [dispatch, filter, debouncedSearch]);

  useEffect(() => { load(); }, [load]);

  const handleCreate = async () => {
    if (!form.title.trim() || submitting) return;
    setSubmitting(true);
    try {
      const doc = await knowledgeBaseAPI.create({
        title: form.title.trim(),
        visibility: form.visibility,
        icon: randomIcon(),
        blocks: [{ id: `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`, type: 'paragraph', content: '' }],
      });
      setForm({ title: '', visibility: 'private' });
      setCreating(false);
      load();
      if (doc?._id) setOpenDocId(doc._id);
    } catch { /* ignore */ }
    setSubmitting(false);
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 px-6 py-3">
        <h1 className="text-xl font-semibold text-gray-900">Knowledge Base</h1>
        <p className="text-sm text-gray-400 mt-0.5">Shared documentation for your team</p>
      </div>

      {/* Search (left) + Filters (center) + New doc (right) */}
      <div className="px-6 py-3 border-b border-gray-100 flex items-center gap-3">
        {/* Search — left */}
        <div className="w-64 shrink-0">
          <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search..."
            className="bg-gray-100 rounded-lg px-3 py-1.5 text-sm w-full outline-none focus:ring-2 focus:ring-blue-500" />
        </div>

        {/* Filters — center */}
        <div className="flex-1 flex justify-center gap-0.5">
          {(['all', 'mine', 'department', 'public'] as const).map(f => (
            <button key={f} onClick={() => setFilter(f)}
              className={`px-2.5 py-1 text-xs rounded-full transition-colors ${filter === f ? 'bg-blue-100 text-blue-700 font-medium' : 'text-gray-400 hover:text-gray-600'}`}>
              {f === 'mine' ? 'My Docs' : cap(f)}
            </button>
          ))}
        </div>

        {/* New doc — right */}
        <button onClick={() => setCreating(true)} className="text-xs text-gray-400 hover:text-blue-600 flex items-center gap-1 shrink-0">
          <span className="text-sm leading-none">+</span> New document
        </button>
      </div>

      {/* Inline create form */}
      {creating && (
        <div className="px-6 py-2.5 border-b border-gray-100 flex items-center gap-2">
          <input autoFocus value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))}
            onKeyDown={e => { if (e.key === 'Enter') handleCreate(); if (e.key === 'Escape') { setCreating(false); setForm({ title: '', visibility: 'private' }); } }}
            placeholder="Document title *" className="flex-1 text-sm py-1 px-0 border-0 border-b border-gray-200 focus:border-blue-500 focus:outline-none bg-transparent" />
          <select value={form.visibility} onChange={e => setForm(f => ({ ...f, visibility: e.target.value }))}
            className="text-xs border border-gray-200 rounded px-1.5 py-1 focus:outline-none focus:border-blue-400">
            <option value="private">Private</option>
            <option value="department">Department</option>
            <option value="team">Team</option>
            <option value="public">Public</option>
          </select>
          <button onClick={handleCreate} disabled={submitting || !form.title.trim()} className="text-xs text-blue-600 hover:text-blue-700 disabled:text-gray-300">Create</button>
          <button onClick={() => { setCreating(false); setForm({ title: '', visibility: 'private' }); }} className="text-xs text-gray-400">Cancel</button>
        </div>
      )}

      {/* Column header */}
      {!isLoading && docs.length > 0 && (
        <div className="grid grid-cols-12 gap-1 px-6 py-2 text-xs text-gray-400 font-medium select-none border-b border-gray-100">
          <div className="col-span-4 flex items-center gap-1">
            <span className="text-gray-300">Aa</span> Name
          </div>
          <div className="col-span-2 flex items-center gap-1">
            <svg className="w-3 h-3 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}><path strokeLinecap="round" strokeLinejoin="round" d="M15.75 6a3.75 3.75 0 11-7.5 0 3.75 3.75 0 017.5 0zM4.501 20.118a7.5 7.5 0 0114.998 0" /></svg>
            People
          </div>
          <div className="col-span-2">Project</div>
          <div className="col-span-1">Access</div>
          <div className="col-span-1">Views</div>
          <div className="col-span-1">Updated</div>
          <div className="col-span-1"></div>
        </div>
      )}

      {/* Docs list */}
      {isLoading ? (
        <div className="flex items-center justify-center py-12"><div className="animate-spin h-5 w-5 border-2 border-blue-500 border-t-transparent rounded-full" /></div>
      ) : docs.length === 0 ? (
        <p className="text-center py-12 text-sm text-gray-400">No documents found</p>
      ) : docs.map(doc => (
        <div key={doc._id}
          className="grid grid-cols-12 gap-1 px-6 py-2.5 border-b border-gray-100 hover:bg-gray-50/80 cursor-pointer transition-colors group/doc text-sm"
          onClick={() => setOpenDocId(doc._id)}>
          {/* Name */}
          <div className="col-span-4 flex items-center gap-2.5 min-w-0">
            <span className="text-lg shrink-0">{doc.icon || '📄'}</span>
            <div className="min-w-0" title={doc.title || 'Untitled'}>
              <span className="text-sm text-gray-700 block truncate capitalize">{(doc.title || 'Untitled').length > 35 ? (doc.title || 'Untitled').slice(0, 35) + '…' : (doc.title || 'Untitled')}</span>
              {doc.description && <span className="text-xs text-gray-300 block truncate">{doc.description.length > 50 ? doc.description.slice(0, 50) + '…' : doc.description}</span>}
            </div>
          </div>
          {/* People — author + collaborators */}
          <div className="col-span-2 flex items-center">
            <div className="flex -space-x-1">
              <div className="w-6 h-6 rounded-full bg-blue-500 text-white text-[10px] flex items-center justify-center border-2 border-white" title={doc.authorName || 'Author'}>
                {(doc.authorName || '?')[0]}
              </div>
              {(doc.collaborators ?? []).slice(0, 2).map((c: any, i: number) => (
                <div key={i} className="w-6 h-6 rounded-full bg-blue-400 text-white text-[10px] flex items-center justify-center border-2 border-white" title={c.name}>
                  {(c.name || '?')[0]}
                </div>
              ))}
              {(doc.collaborators ?? []).length > 2 && (
                <div className="w-6 h-6 rounded-full bg-gray-200 text-gray-500 text-[10px] flex items-center justify-center border-2 border-white">+{doc.collaborators.length - 2}</div>
              )}
            </div>
          </div>
          {/* Project */}
          <div className="col-span-2 flex items-center">
            {(doc.linkedProjects ?? []).length > 0 ? (
              <span className="text-[10px] px-2 py-0.5 bg-blue-50 text-blue-600 rounded-full truncate max-w-full"
                title={doc.linkedProjects.map((p: any) => typeof p === 'object' ? p.name : p).join(', ')}>
                {typeof doc.linkedProjects[0] === 'object' ? (doc.linkedProjects[0] as any).name : `${doc.linkedProjects.length} project${doc.linkedProjects.length > 1 ? 's' : ''}`}
              </span>
            ) : <span className="text-xs text-gray-300">—</span>}
          </div>
          {/* Access */}
          <div className="col-span-1 flex items-center">
            <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${VIS_COLORS[doc.visibility] || 'bg-gray-100 text-gray-600'}`}>{cap(doc.visibility)}</span>
          </div>
          {/* Views */}
          <div className="col-span-1 flex items-center text-xs text-gray-400">
            {doc.viewCount || 0}
          </div>
          {/* Updated */}
          <div className="col-span-1 flex items-center text-xs text-gray-400">
            {timeAgo(doc.updatedAt)}
          </div>
          {/* Actions */}
          <div className="col-span-1 flex items-center justify-end gap-1 opacity-0 group-hover/doc:opacity-100 transition-opacity">
            <button onClick={(e) => { e.stopPropagation(); setOpenDocId(doc._id); }} title="Edit"
              className="text-gray-400 hover:text-blue-600 p-0.5">
              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}><path strokeLinecap="round" strokeLinejoin="round" d="M16.862 4.487l1.687-1.688a1.875 1.875 0 112.652 2.652L10.582 16.07a4.5 4.5 0 01-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 011.13-1.897l8.932-8.931z" /></svg>
            </button>
            <button onClick={async (e) => { e.stopPropagation(); if (window.confirm(`Delete "${doc.title}"?`)) { try { await knowledgeBaseAPI.delete(doc._id); load(); } catch {} } }} title="Delete"
              className="text-gray-400 hover:text-red-500 p-0.5">
              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}><path strokeLinecap="round" strokeLinejoin="round" d="M14.74 9l-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 01-2.244 2.077H8.084a2.25 2.25 0 01-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 00-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 013.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 00-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 00-7.5 0" /></svg>
            </button>
          </div>
        </div>
      ))}

      {/* KB editor modal */}
      {openDocId && (
        <div className="!mt-0 fixed inset-0 z-50 flex items-center justify-center p-4" onClick={() => { setOpenDocId(null); load(); }}>
          <div className="absolute inset-0 bg-black/30" style={{ animation: 'backdropFadeIn 300ms ease-out' }} />
          <div className="relative bg-white rounded-2xl shadow-2xl w-[90vw] h-[90vh] flex flex-col overflow-hidden border border-gray-200" style={{ animation: 'modalFadeIn 300ms ease-out' }} onClick={e => e.stopPropagation()}>
            <KBEditorModal docId={openDocId} onClose={() => { setOpenDocId(null); load(); }} />
          </div>
        </div>
      )}
    </div>
  );
};

export default KnowledgeBaseList;
